/**
 * 🔗 AWS S3 Redirect Configuration Component
 * Für Segment-Config Modal
 */

import React, { useState, useEffect } from 'react';
import { Link, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  AWS_REGION_OPTIONS,
  MULTI_CAMPAIGN_EXCLUDED_AWS_REGIONS,
  MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS,
} from '../../constants/awsRegions';

const DEFAULT_AWS_REGION = MULTI_CAMPAIGN_DEFAULT_AWS_REGIONS[0] || 'ireland';
const EXCLUDED_AWS_REGIONS = new Set(MULTI_CAMPAIGN_EXCLUDED_AWS_REGIONS);
const EXTRA_AWS_REGION_OPTIONS = [
  { value: 'virginia', label: '🇺🇸 Virginia (us-east-1)' },
  { value: 'california', label: '🇺🇸 California (us-west-1)' },
];
const AVAILABLE_AWS_REGION_OPTIONS = [
  ...AWS_REGION_OPTIONS.filter((option) => !EXCLUDED_AWS_REGIONS.has(option.value)),
  ...EXTRA_AWS_REGION_OPTIONS,
];

const AwsRedirectConfig = ({ segment, onUpdate, isAdmin }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [useAllDomains, setUseAllDomains] = useState(true);
  const [linksPerMode, setLinksPerMode] = useState('per_segment'); // 'per_segment' or 'per_slot'
  const [region, setRegion] = useState(DEFAULT_AWS_REGION); // AWS Region
  
  // Lade Slots beim Mount
  useEffect(() => {
    if (isAdmin) {
      loadSlots();
    }
  }, [isAdmin]);
  
  // Lade existierende Konfiguration
  useEffect(() => {
    const awsConfig = segment.config?.aws_redirects || {};
    if (awsConfig.enabled && awsConfig.slot) {
      setSelectedSlot(awsConfig.slot);
      setSelectedDomains(awsConfig.domains || []);
      setUseAllDomains(awsConfig.domains?.length === awsConfig.slot?.domains?.length);
      setLinksPerMode(awsConfig.links_per_mode || 'per_segment');
      setRegion(
        EXCLUDED_AWS_REGIONS.has(awsConfig.region)
          ? DEFAULT_AWS_REGION
          : (awsConfig.region || DEFAULT_AWS_REGION)
      );
    }
  }, [segment]);
  
  const loadSlots = async () => {
    setLoading(true);
    console.log('Loading AWS redirect slots...');
    try {
      const response = await axios.get('/api/aws-redirect/slots');
      console.log('Slots response:', response.data);
      const loadedSlots = response.data.data?.slots || response.data.slots || [];
      setSlots(loadedSlots);
      console.log('Loaded slots:', loadedSlots);
      if (loadedSlots.length > 0) {
        toast.success(`✅ ${loadedSlots.length} Redirect-Slots geladen`);
      }
    } catch (error) {
      console.error('Failed to load redirect slots:', error);
      toast.error('Fehler beim Laden der Redirect-Slots: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSlotChange = (slotIndex) => {
    const slot = slots[slotIndex];
    setSelectedSlot(slot);
    setSelectedDomains(slot.domains); // Default: alle
    setUseAllDomains(true);
  };
  
  const toggleDomain = (domain) => {
    if (selectedDomains.includes(domain)) {
      setSelectedDomains(selectedDomains.filter(d => d !== domain));
    } else {
      setSelectedDomains([...selectedDomains, domain]);
    }
    setUseAllDomains(false);
  };
  
  const handleSave = () => {
    const config = {
      enabled: true,
      slot: selectedSlot,
      domains: useAllDomains ? selectedSlot.domains : selectedDomains,
      links_per_mode: linksPerMode,
      region: region,
      links: null  // Wird später beim Campaign-Start generiert
    };
    
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.aws_redirects = config;
    
    onUpdate(updated);
    toast.success('AWS-Redirect Konfiguration gespeichert');
  };
  
  const handleDisable = () => {
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.aws_redirects = { enabled: false };
    
    onUpdate(updated);
    toast.success('AWS-Redirects deaktiviert');
  };
  
  // Debug
  console.log('AwsRedirectConfig rendered:', { isAdmin, segment: segment?.provider_name });
  
  if (!isAdmin) {
    console.log('AwsRedirectConfig: Not admin, hiding');
    return null; // Nur für Admins sichtbar
  }
  
  const awsConfig = segment.config?.aws_redirects || {};
  const isEnabled = awsConfig.enabled;
  
  console.log('AwsRedirectConfig: Showing UI', { isEnabled, slotsCount: slots.length });
  
  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border-2 border-blue-300 dark:border-blue-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Link className="h-5 w-5 mr-2 text-blue-600" />
          🔗 AWS S3 Redirects (Admin)
        </h3>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isEnabled || false}
            onChange={(e) => {
              if (e.target.checked) {
                // Aktivieren - zeige UI
                const updated = { ...segment };
                if (!updated.config) updated.config = {};
                if (!updated.config.aws_redirects) updated.config.aws_redirects = {};
                updated.config.aws_redirects.enabled = true;
                onUpdate(updated);
              } else {
                // Deaktivieren
                handleDisable();
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-200">Aktiviert</span>
        </div>
      </div>
      
      {(isEnabled || isEnabled === undefined) && (
        <div className="space-y-4">
          {/* Slot-Auswahl */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Redirect-Slot wählen
            </label>
            {loading ? (
              <div className="text-sm text-gray-500">Lade Slots...</div>
            ) : slots.length > 0 ? (
              <select
                value={selectedSlot ? slots.indexOf(selectedSlot) : ''}
                onChange={(e) => handleSlotChange(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
              >
                <option value="">-- Slot wählen --</option>
                {slots.map((slot, idx) => (
                  <option key={idx} value={idx}>
                    {slot.type === 'landing' ? '🌐' : '🔀'} {slot.target_url} ({slot.type === 'landing' ? `Landing · ${slot.total_domains} Domains` : `${slot.total_domains} Domains`})
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-red-600">Keine Slots verfügbar</div>
            )}
          </div>
          
          {/* Domain-Auswahl */}
          {selectedSlot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Domains nutzen
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useAllDomains}
                    onChange={(e) => {
                      setUseAllDomains(e.target.checked);
                      if (e.target.checked) setSelectedDomains(selectedSlot.domains);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    ✅ Alle {selectedSlot.domains.length} Domains nutzen
                  </span>
                </label>
                
                {!useAllDomains && (
                  <div className="ml-6 space-y-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-dark-copper rounded p-2">
                    {selectedSlot.domains.map((domain, idx) => (
                      <label key={idx} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedDomains.includes(domain)}
                          onChange={() => toggleDomain(domain)}
                          className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-xs text-gray-700 dark:text-gray-200">{domain}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedDomains.length} Domain(s) ausgewählt
                </p>
              </div>
            </div>
          )}
          
          {/* AWS Region */}
          {selectedSlot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                AWS Region
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
              >
                {AVAILABLE_AWS_REGION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Links pro Segment/Slot */}
          {selectedSlot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Link-Erstellung
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="per_segment"
                    checked={linksPerMode === 'per_segment'}
                    onChange={(e) => setLinksPerMode(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    📦 Pro Segment (ein Link-Set für alle Slots)
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="per_slot"
                    checked={linksPerMode === 'per_slot'}
                    onChange={(e) => setLinksPerMode(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    🎯 Pro Slot (eigenes Link-Set pro Slot)
                  </span>
                </label>
              </div>
            </div>
          )}
          
          {/* Info Box */}
          {selectedSlot && (
            <div className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded text-xs text-blue-800 dark:text-blue-200">
              <div className="font-semibold mb-1">ℹ️ AWS-Redirect Placeholders:</div>
              <div className="space-y-1 font-mono">
                <div>• <strong>{'{{AWSLOGO}}'}</strong> - Tracking Pixel (S3 → {selectedSlot.target_url}/logo)</div>
                <div>• <strong>{'{{AWSCTA}}'}</strong> - Call-to-Action (S3 → {selectedSlot.target_url})</div>
                <div>• <strong>{'{{AWSUNSUBSCRIBE}}'}</strong> - Unsubscribe (S3 → {selectedSlot.target_url}/unsubscribe)</div>
              </div>
              <div className="mt-2 text-xs">
                Links werden beim Campaign-Start automatisch generiert und auf {selectedDomains.length} Domain(s) verteilt.
              </div>
            </div>
          )}
          
          {/* Save Button */}
          {selectedSlot && selectedDomains.length > 0 && (
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2"
            >
              <CheckCircle className="h-4 w-4" />
              <span>AWS-Redirect Konfiguration speichern</span>
            </button>
          )}
        </div>
      )}
      
      {/* Status Anzeige */}
      {isEnabled && awsConfig.slot && (
        <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-800 dark:text-green-200">
          ✅ Konfiguriert: {awsConfig.slot.target_url} mit {awsConfig.domains?.length || 0} Domain(s)
        </div>
      )}
    </div>
  );
};

export default AwsRedirectConfig;

